import animalsSkills from '../content/animals/skillGraph.json';
import roboticsSkills from '../content/robotics/skillGraph.json';
import mathSkills from '../content/math/skillGraph.json';
import animalsPaths from '../content/animals/paths.json';
import roboticsPaths from '../content/robotics/paths.json';
import mathPaths from '../content/math/paths.json';
import spaceSkills from '../content/space/skillGraph.json';
import spacePaths from '../content/space/paths.json';
import pebblesSkills from '../content/pebbles/skillGraph.json';
import pebblesPaths from '../content/pebbles/paths.json';
import { makeLessonKey } from './progressStore';

/**
 * Skill graph + path-as-lesson-series helpers (no backend; pure JSON lookup).
 *
 * A "skill" is a single learnable outcome backed by exactly one lesson.
 * `requires` lists prerequisite skill ids — a skill is unlocked only when
 * the lessons backing every required skill are completed.
 *
 * A "path" is an ordered list of lessonIds. Today most paths have a single
 * lesson; the structure is in place for series of lessons later.
 */

const SKILL_GRAPHS = {
  animals: animalsSkills,
  robotics: roboticsSkills,
  math: mathSkills,
  space: spaceSkills,
  pebbles: pebblesSkills,
};

const PATHS = {
  animals: animalsPaths,
  robotics: roboticsPaths,
  math: mathPaths,
  space: spacePaths,
  pebbles: pebblesPaths,
};

/** @param {string} worldId */
export function getSkillGraph(worldId) {
  return SKILL_GRAPHS[worldId] || null;
}

/** @param {string} worldId */
export function getPathsBundle(worldId) {
  return PATHS[worldId] || null;
}

/**
 * Return the ordered lessonIds for a path. Falls back to `[pathId]` (single-lesson)
 * when no explicit path entry exists.
 * @param {string} worldId
 * @param {string} pathId
 * @returns {string[]}
 */
export function getPathLessons(worldId, pathId) {
  const bundle = getPathsBundle(worldId);
  if (!bundle?.paths) return pathId ? [pathId] : [];
  const entry = bundle.paths.find((p) => p.id === pathId);
  if (!entry) return pathId ? [pathId] : [];
  if (!Array.isArray(entry.lessons)) return [];
  return entry.lessons.slice();
}

/**
 * Next lesson in the same learning path series (paths.json), if any.
 * If `currentLessonId` appears in multiple paths, the first matching path in bundle order wins.
 * @param {string} worldId
 * @param {string} currentLessonId
 * @returns {{ pathId: string, nextLessonId: string } | null}
 */
export function getNextLessonInSeries(worldId, currentLessonId) {
  const bundle = getPathsBundle(worldId);
  if (!bundle?.paths || !currentLessonId) return null;
  for (const path of bundle.paths) {
    const lessons = Array.isArray(path.lessons) ? path.lessons : [];
    const idx = lessons.indexOf(currentLessonId);
    if (idx >= 0 && idx < lessons.length - 1) {
      return { pathId: path.id, nextLessonId: lessons[idx + 1] };
    }
  }
  return null;
}

/** @param {string} worldId */
export function getSkillsForWorld(worldId) {
  const g = getSkillGraph(worldId);
  if (!g?.skills) return [];
  return g.skills.slice();
}

/** @param {string} worldId @param {string} skillId */
export function getSkillById(worldId, skillId) {
  return getSkillsForWorld(worldId).find((s) => s.id === skillId) || null;
}

/** @param {string} worldId @param {string} lessonId */
export function getSkillForLesson(worldId, lessonId) {
  return getSkillsForWorld(worldId).find((s) => s.lessonId === lessonId) || null;
}

/**
 * Returns true if every prerequisite skill's lesson is completed.
 * @param {string} worldId
 * @param {string} skillId
 * @param {Set<string> | string[]} completedLessonKeys
 */
export function isSkillUnlocked(worldId, skillId, completedLessonKeys) {
  const skill = getSkillById(worldId, skillId);
  if (!skill) return true;
  const required = Array.isArray(skill.requires) ? skill.requires : [];
  if (required.length === 0) return true;
  const completed = completedLessonKeys instanceof Set
    ? completedLessonKeys
    : new Set(completedLessonKeys);
  return required.every((reqSkillId) => {
    const req = getSkillById(worldId, reqSkillId);
    if (!req) return true; // unknown prereq — fail open to avoid hard locks on bad authoring
    return completed.has(makeLessonKey(worldId, req.lessonId));
  });
}

/**
 * Returns the locked status for the lesson behind the given (worldId, pathId, lessonId)
 * by looking up its skill in the world graph.
 */
export function isLessonUnlockedByPrereqs(worldId, lessonId, completedLessonKeys) {
  const skill = getSkillForLesson(worldId, lessonId);
  if (!skill) return true;
  return isSkillUnlocked(worldId, skill.id, completedLessonKeys);
}

/**
 * Topological order. Throws if a cycle is detected.
 * @param {{ skills: Array<{id:string, requires?:string[]}> }} graph
 */
export function topoSort(graph) {
  const skills = graph?.skills || [];
  const idx = new Map(skills.map((s, i) => [s.id, i]));
  const visited = new Set();
  const inStack = new Set();
  const out = [];
  const visit = (id) => {
    if (visited.has(id)) return;
    if (inStack.has(id)) throw new Error(`Cycle in skill graph at "${id}"`);
    inStack.add(id);
    const s = skills[idx.get(id)];
    for (const r of s?.requires || []) {
      if (idx.has(r)) visit(r);
    }
    inStack.delete(id);
    visited.add(id);
    out.push(s);
  };
  for (const s of skills) visit(s.id);
  return out;
}

/**
 * Lightweight validator usable from both the app and the validate script.
 * @returns {{ errors: string[], warnings: string[] }}
 */
export function validateSkillGraph(graph, knownLessonIds = []) {
  const errors = [];
  const warnings = [];
  if (!graph || typeof graph !== 'object') {
    errors.push('Skill graph root must be an object');
    return { errors, warnings };
  }
  if (!Array.isArray(graph.skills)) {
    errors.push('Skill graph requires a skills[] array');
    return { errors, warnings };
  }
  const ids = new Set();
  graph.skills.forEach((s, i) => {
    const loc = `skill ${i + 1} (${s?.id ?? '?'})`;
    if (!s?.id) errors.push(`${loc}: id is required`);
    else if (ids.has(s.id)) errors.push(`${loc}: duplicate skill id "${s.id}"`);
    else ids.add(s.id);
    if (!s?.title) warnings.push(`${loc}: title is empty`);
    if (!s?.lessonId) errors.push(`${loc}: lessonId is required`);
    else if (knownLessonIds.length > 0 && !knownLessonIds.includes(s.lessonId)) {
      warnings.push(`${loc}: lessonId "${s.lessonId}" is not in the known lesson list yet`);
    }
  });
  graph.skills.forEach((s) => {
    for (const r of s?.requires || []) {
      if (!ids.has(r)) {
        errors.push(`skill "${s.id}": requires unknown skill "${r}"`);
      }
    }
  });
  try {
    topoSort(graph);
  } catch (e) {
    errors.push(e.message || String(e));
  }
  return { errors, warnings };
}

/**
 * Validate a paths.json bundle. References to lessons are checked against
 * an optional set of known lessonIds.
 */
export function validatePathsBundle(bundle, knownLessonIds = []) {
  const errors = [];
  const warnings = [];
  if (!bundle || typeof bundle !== 'object') {
    errors.push('Paths root must be an object');
    return { errors, warnings };
  }
  if (!Array.isArray(bundle.paths)) {
    errors.push('Paths bundle requires paths[]');
    return { errors, warnings };
  }
  const ids = new Set();
  bundle.paths.forEach((p, i) => {
    const loc = `path ${i + 1} (${p?.id ?? '?'})`;
    if (!p?.id) errors.push(`${loc}: id is required`);
    else if (ids.has(p.id)) errors.push(`${loc}: duplicate path id "${p.id}"`);
    else ids.add(p.id);
    if (!Array.isArray(p?.lessons)) {
      errors.push(`${loc}: lessons must be an array`);
    } else {
      p.lessons.forEach((lid, li) => {
        if (typeof lid !== 'string' || lid.trim() === '') {
          errors.push(`${loc}: lesson ${li + 1} must be a non-empty string`);
        } else if (knownLessonIds.length > 0 && !knownLessonIds.includes(lid)) {
          warnings.push(`${loc}: lessonId "${lid}" is not in the known lesson list yet`);
        }
      });
    }
  });
  return { errors, warnings };
}
